import { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";

export const ContactForm = () => {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", email: "", message: "", company: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success(t("form.success"));
      setForm({ name: "", email: "", message: "", company: "" });
    } catch {
      toast.error(t("form.error"));
    } finally {
      setSending(false);
    }
  };

  const fieldClass =
    "w-full bg-transparent border border-gold/25 focus:border-gold px-5 py-4 text-sm font-light text-white placeholder:text-white/35 outline-none transition-colors duration-300";

  return (
    <div
      data-testid="contact-form-block"
      className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16"
    >
      <div className="md:col-span-5">
        <span className="text-[11px] tracking-[0.4em] uppercase text-gold">
          {t("form.kicker")}
        </span>
        <h3
          data-testid="contact-form-heading"
          className="mt-4 font-display text-3xl md:text-4xl leading-[1.05] tracking-tight"
        >
          {t("form.heading")}
        </h3>
        <p className="mt-4 text-white/50 text-sm font-light">{t("form.sub")}</p>
      </div>

      <form
        data-testid="contact-form"
        onSubmit={onSubmit}
        className="md:col-span-7 flex flex-col gap-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input
            data-testid="contact-form-name-input"
            type="text"
            name="name"
            required
            maxLength={100}
            value={form.name}
            onChange={onChange}
            placeholder={t("form.name")}
            aria-label={t("form.name")}
            className={fieldClass}
          />
          <input
            data-testid="contact-form-email-input"
            type="email"
            name="email"
            required
            value={form.email}
            onChange={onChange}
            placeholder={t("form.email")}
            aria-label={t("form.email")}
            className={fieldClass}
          />
        </div>
        <textarea
          data-testid="contact-form-message-input"
          name="message"
          required
          maxLength={2000}
          rows={4}
          value={form.message}
          onChange={onChange}
          placeholder={t("form.message")}
          aria-label={t("form.message")}
          className={`${fieldClass} resize-none`}
        />
        {/* Honeypot — invisible to humans, bots fill it */}
        <input
          type="text"
          name="company"
          value={form.company}
          onChange={onChange}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="hidden"
        />
        <div>
          <button
            data-testid="contact-form-submit-button"
            type="submit"
            disabled={sending}
            className="group inline-flex items-center gap-3 border border-gold bg-gold text-black px-8 py-4 text-[11px] tracking-[0.35em] uppercase hover:bg-transparent hover:text-gold transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? t("form.sending") : t("form.submit")}
            <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
